/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { getIn, useFormikContext } from "formik";
import { useToastContext } from "../toast/toast.context";
import { SignatureInfo } from "@/types/renter.type";
import { ImageBondVehicleRenterRow } from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/ImageBondVehicleRenterRow";
import ButtonWithLoader from "../loading/ButtonLoading";
import VelotrackActivate from "@/modules/renters/components/modals/fields/UploadInfoApis/VelotrackActivate";
import ZapSignActivate from "@/modules/renters/components/modals/fields/UploadInfoApis/ZapSignActivate";
import ZapSignConfirmation from "@/modules/renters/components/modals/fields/UploadInfoApis/ZapSignConfirmation";
import { createContract } from "@/modules/renters/services/integration.api";

type VehicleBondingFlowProps = {
  renterId: string;
  renterName: string;
  onSavePhotos?: (data: SavePhotosData) => Promise<{ ok: boolean; message?: string }>;
  onBond?: (data: BondData) => Promise<{ ok: boolean; message?: string }>;
  showVehicleSelector?: boolean;
  vehicleSelectorComponent?: React.ReactNode;
  additionalFields?: React.ReactNode;
  contractData: {
    renterId: string;
    vehicleId: string;
    period: any;
    additions?: any[];
  };
  registersFieldName?: string;
  vehicleFieldName?: string;
  buttonLabel?: string;
  showPhotosDeliveryWarning?: boolean;
  onSuccess?: () => void;
};

type SavePhotosData = {
  renterId: string;
  vehicleId: string;
  photos: any[];
  nameRenter: string;
  licensePlate: string;
  switchDescription?: string;
  switchReason?: string;
};

type BondData = {
  renterId: string;
  vehicleId: string;
  photos: Array<{ type: string; content: string }>;
  isCheckOption?: boolean;
};

export default function VehicleBondingFlow(props: VehicleBondingFlowProps) {
  const {
    renterId,
    renterName,
    onSavePhotos,
    onBond,
    showVehicleSelector = false,
    vehicleSelectorComponent,
    additionalFields,
    contractData,
    registersFieldName = "registers",
    vehicleFieldName = "vehicle",
    buttonLabel = "Salvar",
    showPhotosDeliveryWarning = true,
    onSuccess,
  } = props;

  const { notifySuccess, notifyError } = useToastContext();
  const formik = useFormikContext<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [bondCompleted, setBondCompleted] = useState(false);
  const [photosDelivery, setPhotosDelivery] = useState(false);
  const [signatureInfo, setSignatureInfo] = useState<SignatureInfo>();

  const registers = getIn(formik.values, registersFieldName);
  const vehicle = getIn(formik.values, vehicleFieldName);
  const vehicleId = vehicle?._id || getIn(formik.values, "vehicleId");

  const buildTouchedTree = (value: any): any => {
    if (Array.isArray(value)) {
      return value.map((item) => buildTouchedTree(item));
    }
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => [
          key,
          buildTouchedTree(nestedValue),
        ])
      );
    }
    return true;
  };

  useEffect(() => {
    if (vehicle?._id && vehicle._id !== formik.values.vehicleId) {
      formik.setFieldValue("vehicleId", vehicle._id);
    } else if (!vehicle && formik.values.vehicleId) {

      formik.setFieldValue("vehicleId", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?._id]);


  useEffect(() => {
    if (registers && registers.length > 0) {
      const allDeliveryPhotos = registers.every(
        (register) => register.content && register.content.includes("entrega")
      );
      setPhotosDelivery(allDeliveryPhotos);
    } else {
      setPhotosDelivery(false);
    }
  }, [registers]);

  const handleSave = async () => {
    setIsLoading(true);
    setBondCompleted(false);

    try {
      const validationErrors = await formik.validateForm();
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(buildTouchedTree(formik.values));
        notifyError("Preencha os campos obrigatórios antes de continuar.");
        return;
      }

      if (onSavePhotos && registers && registers.length > 0) {
        const savePhotoData: SavePhotosData = {
          renterId,
          vehicleId: vehicleId || "",
          photos: registers,
          nameRenter: renterName,
          licensePlate: vehicle?.licensePlate || "",
          switchDescription: getIn(formik.values, "complement"),
          switchReason: getIn(formik.values, "reason"),
        };

        const resultPhoto = await onSavePhotos(savePhotoData);

        if (!resultPhoto.ok) {
          notifyError(resultPhoto.message as string);
          return;
        }

        notifySuccess("Fotos salvas com sucesso");
      }


      if (onBond) {
        const bondData: BondData = {
          renterId,
          vehicleId: vehicleId || "",
          photos: registers,
          isCheckOption: getIn(formik.values, "isCheckOption"),
        };

        const resultBond = await onBond(bondData);

        if (!resultBond.ok) {
          notifyError(resultBond.message as string);
          return;
        }

        notifySuccess("Veículo vinculado com sucesso");
      }

      setBondCompleted(true);
      onSuccess && onSuccess();
    } catch (error) {
      notifyError("Ocorreu um erro ao processar");
      setBondCompleted(false);
    } finally {
      setIsLoading(false);
    }
  };

  function handleSignature(contractInfo) {
    setSignatureInfo(contractInfo);
  }

  return (
    <section className="space-y-4">
      {/* Campos adicionais (motivo, complemento, etc) */}
      {additionalFields}

      {/* Seletor de veículo (opcional) */}
      {showVehicleSelector && vehicleSelectorComponent}

      {/* Registros do veículo */}
      <div className="grid gap-2">
        <h2 className="text-2xl font-semibold">Registros do veículo</h2>

        {(!vehicle || !vehicleId) && (
          <small className="text-redPrimary">
            Selecione um veículo para adicionar as fotos
          </small>
        )}

        {vehicle && vehicleId && (
          <section>
            {registers?.map((doc, index) => (
              <div
                key={index}
                className={`space-x-3 px-3 py-2 my-0 rounded ${index % 2 ? "bg-gray/5" : "bg-gray/25"
                  }`}
              >
                <ImageBondVehicleRenterRow
                  initialData={doc || {}}
                  name={`${registersFieldName}.${index}`}
                  onRemoveClick={() => {
                    const updatedRegisters = [...registers];
                    updatedRegisters.splice(index, 1);
                    formik.setFieldValue(registersFieldName, updatedRegisters);
                  }}
                  prefix="entrega"
                />
              </div>
            ))}

            <div className="flex justify-center">
              <button
                type="button"
                className="disabled:text-black/50"
                disabled={!vehicle || !vehicleId}
                onClick={() => {
                  const newRegisters = [...(registers || [])];
                  newRegisters.push({ type: "", content: "" });
                  formik.setFieldValue(registersFieldName, newRegisters);
                }}
              >
                + registro
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Botão de salvar */}
      <section>
        <div className="flex justify-end mb-2 mt-4">
          <ButtonWithLoader
            type="button"
            onClick={handleSave}
            isLoading={isLoading}
            disabled={
              !vehicle ||
              !vehicleId ||
              !formik.isValid ||
              (showPhotosDeliveryWarning && !photosDelivery) ||
              bondCompleted
            }
          >
            {buttonLabel}
          </ButtonWithLoader>
        </div>

        {showPhotosDeliveryWarning && !photosDelivery && (
          <div className="flex justify-end">
            <p className="text-redPrimary text-[14px]">
              As fotos de entrega do veículo devem ser adicionadas nos registros.
            </p>
          </div>
        )}
      </section>

      {/* Velotrack */}
      <div className="mt-4">
        <VelotrackActivate
          disabled={!vehicle || !vehicleId || !bondCompleted}
          onSave={() => { }}
          renterId={renterId}
        />
      </div>

      {/* ZapSign */}
      <div className="mt-4">
        <ZapSignActivate
          disabled={!vehicle || !vehicleId || !bondCompleted}
          onSave={() => { }}
          contractData={contractData}
          handleSignature={handleSignature}
          contractFn={createContract}
        />
      </div>

      {/* Confirmação do contrato */}
      {signatureInfo?.ok && (
        <div className="mt-4">
          <ZapSignConfirmation contractInfo={signatureInfo} />
        </div>
      )}
    </section>
  );
}
